import { takeUntil, filter, take } from 'rxjs/operators';
import { ReportSession } from '@sas-dvr/ltjs-vav';
import { IReport, ReportReadyState } from './IReport';
import { ltjsProperty, ltjsDestructor } from '../../ltjs-decorators';
import {
  Report,
  ReportAssetURLType,
  ReportManifest,
  FileType,
  ReportMetadata,
} from '@sas-dvr/ltjs-transport';
import { ReplaySubject, Observable, BehaviorSubject } from 'rxjs';
import { embeddedBundle } from '@sas-dvr/internal-va-react-core/locale/embeddedI18N';
import {
  CachedReportURL,
  CachedReportURLType,
  MaxRowsLimit,
  MaxRowsLookup,
  ViewSet,
} from '@sas-dvr/ltjs-bird';
import { ltjsModule } from '@sas-dvr/ltjs-commons/ltjsEnvironment';
import { LocalServer } from './LocalServer';
import { getUseHighContrastReportTheme } from '@sas-dvr/internal-va-react-core/config';

const genericErrorMessage = embeddedBundle['SASReport.genericError.msg']();

function _getCachedReportURLsFromManifest(manifest: ReportManifest) {
  const urls = manifest.getUrls();
  const cachedReportURLVector = [];
  if (urls) {
    for (const url of urls) {
      if (!url) {
        continue;
      }
      const assetType = url.getType();
      if (assetType !== ReportAssetURLType.STP && assetType !== ReportAssetURLType.IMAGE) {
        continue;
      }

      const cachedReportURL = new CachedReportURL();
      cachedReportURL.setName(url.getName());
      cachedReportURL.setType(CachedReportURLType.STP);
      cachedReportURL.setContentKey(url.getContentKey());
      cachedReportURL.setServerFile(url.getServerFile());
      cachedReportURL.setContent(url.getUrlString());

      cachedReportURLVector.push(cachedReportURL);
    }
  }

  return cachedReportURLVector;
}

function _getContentKeyFromManifest(manifest: ReportManifest) {
  //
  // R9 and earlier we simply search through manifest's urls
  //
  const urls = manifest.getUrls();
  if (urls) {
    for (let urlIndex = 0; urlIndex < urls.length; ++urlIndex) {
      const reportAssetURL = urls[urlIndex];
      if (reportAssetURL && reportAssetURL.getType() === ReportAssetURLType.REPORT) {
        return reportAssetURL.getContentKey();
      }
    }
  }

  //
  // R10 and later we check the manifest's files
  //
  const files = manifest.getFiles();
  if (files) {
    for (let fileIndex = 0; fileIndex < files.length; ++fileIndex) {
      const file = files[fileIndex];
      if (file && file.getType() === FileType.REPORT) {
        return file.getContentKey();
      }
    }
  }

  return null;
}

function joinPath(...paths: string[]) {
  let path = '';
  for (let partial of paths) {
    if (!partial) {
      continue;
    }
    if (partial.charAt(partial.length - 1) !== '/') {
      partial += '/';
    }
    if (path) {
      if (partial.indexOf('./') === 0) {
        partial = partial.substr(2);
      } else if (partial.charAt(0) === '/') {
        partial = partial.substr(1);
      }
    }
    path += partial;
  }
  return path.substr(0, path.length - 1);
}

function downloadResource(url: string, filePath: string | undefined, signal: AbortSignal) {
  return fetch(url, { signal })
    .then(function (response: Response) {
      if (!response.ok) {
        throw new Error(`Invalid response code: ${response.status} for ${url}`);
      }
      return response.text();
    })
    .then((value: string) => {
      if (!filePath) {
        return value;
      }
      const exists = ltjsModule.exports.FS.findObject(filePath, true);
      if (!exists) {
        const pathNodes = filePath.split('/').filter((value) => !!value);
        // skip file name
        pathNodes.pop();

        let dirPath = '';
        for (const dir of pathNodes) {
          dirPath += '/' + dir;
          try {
            ltjsModule.exports.FS.mkdir(dirPath);
          } catch (e) {
            // ignore EEXIST
          }
        }
      }
      // deepcode ignore PT: Writing to a virtual FS, so the Path Traversal vulnerability does not apply.
      ltjsModule.exports.FS.writeFile(filePath, value);
      return value;
    });
}

const SETTINGS_DEFAULTS = {
  attributes: {
    comparisonEpsilon: '1e-12',
    iframeSandbox: 'allow-forms allow-presentation allow-same-origin allow-scripts',
    'maxRowsLookup.treeMap': 1500,
    'maxRowsLookup.graphDefault': 3000,
    'maxRowsLookup.heatmap': 3000,
    'maxRowsLookup.dropdown': 500,
    'maxRowsLookup.timeSeries': 10000,
    'maxRowsLookup.bubble': 25000,
    'maxRowsLookup.listTable': 40000,
    'maxRowsLookup.geoBubble': 25000,
    'maxRowsLookup.keyValue': 100,
    'maxRowsLookup.geoScatter': 40000,
    'maxRowsLookup.heatbox': 800,
    'maxRowsLookup.buttonBar': 100,
    'maxRowsLookup.dualAxisTimeSeries': 10000,
    'maxRowsLookup.crosstab': 40000,
    'maxRowsLookup.geoHeatmap': 25000,
    'maxRowsLookup.geoLine': 10000,
    'maxRowsLookup.geoRegion': 5000,
    'maxRowsLookup.list': 50000,
    'maxRowsLookup.scatter': 40000,
    'maxRowsLookup.textInput': 40000,
    'maxRowsLookup.kpi': 10,
    'maxRowsLookup.wordCloud': 100,
    'maxRowsLookup.customContent': 40000,
    'maxRowsLookup.geoContour': 40000,
  },
};

function keyToMaxRowsLookupEnum(key: string) {
  if (!key.startsWith('maxRowsLookup.')) {
    return undefined;
  }
  const enumKey = key.split('.')[1].toUpperCase();
  switch (enumKey) {
    case 'NONE':
      return MaxRowsLookup.NONE;
    case 'LISTTABLE':
      return MaxRowsLookup.LISTTABLE;
    case 'CROSSTAB':
      return MaxRowsLookup.CROSSTAB;
    case 'BUTTONBAR':
      return MaxRowsLookup.BUTTONBAR;
    case 'CUSTOMCONTENT':
      return MaxRowsLookup.CUSTOMCONTENT;
    case 'DROPDOWN':
      return MaxRowsLookup.DROPDOWN;
    case 'LIST':
      return MaxRowsLookup.LIST;
    case 'TEXTINPUT':
      return MaxRowsLookup.TEXTINPUT;
    case 'GRAPHDEFAULT':
      return MaxRowsLookup.GRAPHDEFAULT;
    case 'BUBBLE':
      return MaxRowsLookup.BUBBLE;
    case 'GEOBUBBLE':
      return MaxRowsLookup.GEOBUBBLE;
    case 'GEOCONTOUR':
      return MaxRowsLookup.GEOCONTOUR;
    case 'GEOSCATTER':
      return MaxRowsLookup.GEOSCATTER;
    case 'GEOREGION':
      return MaxRowsLookup.GEOREGION;
    case 'GEOHEATMAP':
      return MaxRowsLookup.GEOHEATMAP;
    case 'GEOLINE':
      return MaxRowsLookup.GEOLINE;
    case 'HEATMAP':
      return MaxRowsLookup.HEATMAP;
    case 'TIMESERIES':
      return MaxRowsLookup.TIMESERIES;
    case 'KPI':
      return MaxRowsLookup.KPI;
    case 'HEATBOX':
      return MaxRowsLookup.HEATBOX;
    case 'DUALAXISTIMESERIES':
      return MaxRowsLookup.DUALAXISTIMESERIES;
    case 'SCATTER':
      return MaxRowsLookup.SCATTER;
    case 'TREEMAP':
      return MaxRowsLookup.TREEMAP;
    case 'WORDCLOUD':
      return MaxRowsLookup.WORDCLOUD;
    case 'KEYVALUE':
      return MaxRowsLookup.KEYVALUE;
  }
  return undefined;
}

export class LocalReport implements IReport {
  @ltjsProperty
  private _report: Report | undefined;

  private _server: LocalServer;
  private _reportUri: string = '';
  private _errorNotifier: ReplaySubject<string | undefined> = new ReplaySubject(1);
  private _currentError: string | undefined;
  private _reportReadyNotifier = new BehaviorSubject<ReportReadyState>('loading');
  private _destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  private _retainCount = 0;
  private _settingAttrs: { [keyof: string]: string | number } | undefined;
  private _reportSubscribed: boolean = false;
  private _highContrastPath: string | undefined;
  private _abortController: AbortController | undefined;

  constructor(server: LocalServer) {
    this._server = server;

    server
      .getErrorNotifier()
      .pipe(takeUntil(this._destroyed$))
      .subscribe((error) => this._setError(error));

    this._subscribe();
  }

  private _subscribe() {
    if (this._reportSubscribed) {
      return;
    }
    this._reportSubscribed = true;
    this._server
      .getStatusNotifier()
      .pipe(
        takeUntil(this._destroyed$),
        filter((status) => status === 'connected'),
        take(1)
      )
      .subscribe(() => this._fetchReport());
  }

  private async _fetchReport() {
    const urlBase = joinPath(this._server.getUrl(), this._reportUri);
    const report = new Report().retain();
    const meta = new ReportMetadata();
    meta.setLocation(urlBase);
    meta.setPath(urlBase);
    report.setMeta(meta);
    report.setConnection(this._server.getConnection());
    this._abortController = new AbortController();
    const signal = this._abortController.signal;

    const fileBase = this._server.getReportManager()?.getReportSubdirectoryPath(report)!;

    await Promise.all([
      fetch(joinPath(urlBase, 'settings.json'), { signal })
        .then((resp) => {
          if (!resp.ok) {
            return Promise.reject(
              `Invalid response code when downloading settings.json. Status code: ${resp.status}`
            );
          }
          return resp.json();
        })
        .catch((e) => {
          console.error(`Unable to download settings.json, Error: ${e}`);
          return SETTINGS_DEFAULTS;
        })
        .then((items) => {
          this._settingAttrs = {
            ...SETTINGS_DEFAULTS.attributes,
            ...items?.attributes,
          };
        }),
      downloadResource(joinPath(urlBase, 'reportPackage.xml'), undefined, signal).then(
        async (xml) => {
          const info = new DOMParser().parseFromString(xml, 'application/xml') as XMLDocument;
          const files = [];
          const acceptableTypes = ['report', 'css', 'resource', 'state'];
          for (const fileEl of info.querySelectorAll('reportPackage files file')) {
            const type = fileEl.getAttribute('type');
            if (!type || !acceptableTypes.includes(type)) {
              continue;
            }
            // settings is handled separately
            if (type === 'resource' && fileEl.getAttribute('name') === 'settings') {
              continue;
            }
            const href = fileEl.querySelector('links link')?.getAttribute('href');
            if (!href) {
              continue;
            }
            const filename = href.substr(href.lastIndexOf('/') + 1);
            if (type === 'css' && fileEl.getAttribute('name') === 'highcontrast.css') {
              if (!getUseHighContrastReportTheme()) {
                continue;
              }
              this._highContrastPath = joinPath(fileBase, filename);
            }
            if (type === 'css' && fileEl.getAttribute('name') === 'viyafonts.css') {
              continue;
            }
            files.push(
              downloadResource(joinPath(urlBase, href), joinPath(fileBase, filename), signal)
            );
          }
          await Promise.all(files);
        }
      ),
    ])
      .finally(() => {
        report.release();
      })
      .catch((e) => {
        this._setError(genericErrorMessage);
        console.error(e);
      });
    this._abortController = undefined;
    if (!this._currentError) {
      this._report = report;
      this._reportReadyNotifier.next('ready');
    }
  }
  /**
   * Stream for notifying changes to the error state of SASReport.  Re-broadcasts SASServer errors and will replay the
   *   latest error state when subscribed.
   */
  getErrorNotifier(): Observable<string | undefined> {
    return this._errorNotifier;
  }

  /**
   * Stream for monitoring the state of the transport report.
   *
   *  loading - stream initial state or when the report is refreshed
   *  saving - the report is in the process of saving user state.  Saving results in a report refresh.
   *    Saving is only used for SASReport and not LocalReport.
   *  ready - report is ready to be rendered
   */
  getReportStateNotifier(): Observable<ReportReadyState> {
    return this._reportReadyNotifier;
  }

  private _setError(error: string | undefined) {
    if (this._currentError === error) {
      return;
    }
    this._currentError = error;
    this._errorNotifier.next(error);
  }

  getReportState() {
    return this._reportReadyNotifier.value;
  }

  getError() {
    return this._currentError || this._server.getError();
  }

  get name() {
    return null;
  }

  get identifierOrLocation() {
    return this._reportUri;
  }

  get description() {
    return null;
  }

  get dateCreated() {
    return null;
  }

  get dateUpdated() {
    return null;
  }

  get author() {
    return null;
  }

  /**
   * @internal
   */
  createSession(viewSet: ViewSet | null, graphCSS?: string) {
    const manager = this._server.getReportManager();
    if (!manager || !this._report) {
      return null;
    }
    const reportSession = new ReportSession(manager, this._report);
    const configuration = reportSession.getSASReportConfiguration()!;
    configuration.setIncludeAllTableColumnsInPickResults(false);
    configuration.setUseSingleThreadMode(false);
    configuration.setUseDefaultGTLDataTips(false);
    configuration.setUseReadOnlyMode(false);
    configuration.setMaximumNumberOfSectionsInMemory(-1);
    configuration.setUseOfflineMode(true);
    configuration.setOfflinePackageUrl(this._server.getUrl());
    if (this._settingAttrs) {
      const { comparisonEpsilon, iframeSandbox } = this._settingAttrs;
      if (comparisonEpsilon) {
        const epsilon =
          typeof comparisonEpsilon === 'number' ? comparisonEpsilon : parseFloat(comparisonEpsilon);
        configuration.setComparisonEpsilon(epsilon);
      }
      // Each report has it's own 'server', so this is fine.
      if (iframeSandbox && typeof iframeSandbox === 'string') {
        this._server.setIframeSandbox(iframeSandbox);
      }
      const limits = [];
      for (const key of Object.keys(this._settingAttrs)) {
        const value = this._settingAttrs[key];
        const limitLookup = keyToMaxRowsLookupEnum(key);
        if (limitLookup !== undefined && typeof value === 'number') {
          const maxRowLimit = new MaxRowsLimit();
          maxRowLimit.setLookup(limitLookup);
          maxRowLimit.setLimit(value);
          limits.push(maxRowLimit);
        }
      }
      configuration.setMaxRowsLimits(limits);
    }

    const reportPath = configuration.getFullyQualifiedPathToBIRDReportDirectory();

    if (getUseHighContrastReportTheme()) {
      if (!this._highContrastPath) {
        console.warn(
          'This report package is incompatible with high contrast mode: no high contrast CSS file found.'
        );
      } else {
        configuration.setDoVisualStyleOverride(true);
        configuration.setReportCSSOverideFile(this._highContrastPath);
      }
    }

    if (graphCSS !== undefined) {
      let baseCssFilePath;
      {
        // Parse report.xml and find the BaseStyleSheet file
        const reportContent = ltjsModule.getStringContentsOfFile(`${reportPath}/report.xml`);
        if (reportContent) {
          const match = reportContent.match(/<BaseStylesheetResource file="(.*?)"/);
          if (match && match.length === 2) {
            baseCssFilePath = `${reportPath}/${match[1]}`;
          }
        }
      }
      if (baseCssFilePath) {
        // Overwrite the base stylesheet file with the custom css
        ltjsModule.exports.FS.writeFile(baseCssFilePath, graphCSS);
      } else {
        console.error('The base stylesheet was not found so the custom css could not be applied');
      }
    }
    const conManager = this._server.getConnectionManager();
    if (!conManager) {
      return null;
    }
    configuration.setMapConnectionInfo(conManager.getMapConnectionInfo());

    const manifest = this._report.getManifest();
    if (manifest) {
      configuration.setCachedReportURLs(_getCachedReportURLsFromManifest(manifest));
      const contentKey = _getContentKeyFromManifest(manifest);
      if (contentKey) {
        configuration.setContentKey(contentKey);
      }
    }

    // null means we are in regular data loading mode.
    // non-null means we are in "viewset" mode.
    configuration.setViewSet(viewSet);
    try {
      reportSession.openReport();
    } catch (e) {
      return null;
    }

    return reportSession;
  }

  getServer() {
    return this._server;
  }

  retain() {
    this._retainCount++;
    return this;
  }

  release() {
    if (this._retainCount <= 0) {
      throw new Error('SASReport released without being retained');
    }
    this._retainCount--;
    if (this._retainCount === 0) {
      this.close();
      this._server.removeReport();
    }
  }

  reload() {
    if (this._report) {
      this._reportSubscribed = false;
      this._subscribe();
    }
    this._report = undefined;
    this._reportReadyNotifier.next('loading');
  }

  @ltjsDestructor
  close() {
    this._abortController?.abort();
    if (this._retainCount !== 0) {
      console.error(`SASReport ${this.identifierOrLocation} was destroyed while in use`);
    }
    this._destroyed$.next(true);
  }
}
